
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { TipTap } from '@/mixins/TipTap';
import {
  emptyValue, enumItems, factcheckPicker, hasEnumPreviews, followsSeries, hiddenAsOutdated, hiddenByFollow, hiddenByLayout, hiddenByMediaKind, isCategoryList, isLinkKey, isRecord, isTechniqueName, itemSummary, labelFor, mediaKind, nullableInner, slugify,
  stringWidget, techniqueKey, techniqueOptions, withMediaKind, FactcheckPicker, JsonSchema, StringWidget, TechniqueOption, ValidationError,
} from '@/content/schema';
import { errorsWithin, isObjectList, isOwnPart } from '@/content/outline';
import { askConfirm } from '@/content/confirm';
import { mediaUrl, SITE_URL } from '@/content/pages';
import CategoryField from './CategoryField.vue';
import FactcheckField from './FactcheckField.vue';
import ImageField from './ImageField.vue';
import FollowedStory from './FollowedStory.vue';
import ItemList from './ItemList.vue';
import LinkField from './LinkField.vue';
import RichTextField from './RichTextField.vue';
import TechniqueField from './TechniqueField.vue';

/**
 * Renders one JSON Schema node as a form field and recurses into objects and arrays. Lists of groups
 * and groups with a place of their own in the outline are not unfolded here: they show as rows or a
 * link, and a click asks the editor to open them (the `open` event, with their path).
 */
@Component({ name: 'SchemaField', components: { CategoryField, FactcheckField, FollowedStory, ImageField, ItemList, LinkField, RichTextField, TechniqueField } })
export default class SchemaField extends mixins(TipTap) {
  @Prop({ required: true }) readonly schema!: JsonSchema;
  @Prop({ required: true }) readonly value!: unknown;
  @Prop({ required: true }) readonly label!: string;
  @Prop({ default: '' }) readonly fieldKey!: string;
  /** Key of the enclosing object, for fields whose own key is too generic (`factchecks.ids`). */
  @Prop({ default: '' }) readonly parentKey!: string;
  @Prop({ required: true }) readonly page!: string;
  @Prop({ default: 0 }) readonly depth!: number;
  @Prop({ type: Boolean, default: false }) readonly required!: boolean;
  @Prop({ type: Boolean, default: false }) readonly hideLabel!: boolean;
  /** Where this field sits in the page, in the notation of `validate` (`series[2].title`). */
  @Prop({ default: '' }) readonly path!: string;
  /** Validation errors of the whole page; each field shows its own. */
  @Prop({ type: Array, default: () => [] }) readonly errors!: ValidationError[];
  /** Layout of the nearest series above (`slides` or `factchecks`); hides the fields of the other one. */
  @Prop({ default: '' }) readonly layout!: string;
  /** The whole page being edited, for fields that offer what the page already holds (the techniques used before). */
  @Prop({ default: null }) readonly pageValue!: unknown;
  /** The page as last saved, to mark list items with unsaved changes. */
  @Prop({ default: null }) readonly savedValue!: unknown;

  dateMenu = false;
  /** The slug this field last filled in from the title; while it is unchanged, it keeps following the title. */
  autoSlug = '';
  /** Drag and drop of plain-text list items: the item whose handle is held, the one being dragged, the one under it. */
  handleDown = -1;
  dragIndex = -1;
  dropIndex = -1;

  get nullable(): JsonSchema | null {
    return nullableInner(this.schema);
  }

  get objectValue(): Record<string, unknown> {
    return isRecord(this.value) ? this.value : {};
  }

  get arrayValue(): unknown[] {
    return Array.isArray(this.value) ? this.value : [];
  }

  get stringValue(): string {
    return typeof this.value === 'string' ? this.value : '';
  }

  get widget(): StringWidget {
    return stringWidget(this.fieldKey, this.schema, this.value);
  }

  /** yyyy-mm-dd shown as dd/mm/yyyy, the way the site writes it. */
  get dateLabel(): string {
    const [year, month, day] = this.stringValue.split('-');
    return year && month && day ? `${day}/${month}/${year}` : '';
  }

  get factcheckPicker(): FactcheckPicker | null {
    return factcheckPicker(this.fieldKey, this.parentKey, this.schema);
  }

  /** A description can hold a link to the item on the site, e.g. a game round's: {site} and {value} are filled in. */
  get help(): string {
    return (this.schema.description || '').replace(/\{site\}/g, SITE_URL).replace(/\{value\}/g, this.stringValue);
  }

  get childLayout(): string {
    return typeof this.objectValue.layout === 'string' ? this.objectValue.layout : this.layout;
  }

  get visibleProperties(): [string, JsonSchema][] {
    return Object.entries(this.schema.properties || {}).filter(
      ([key, property]) =>
        !hiddenByLayout(key, this.childLayout) &&
        !hiddenByFollow(key, this.schema, this.value) &&
        !hiddenByMediaKind(key, this.schema, this.value) &&
        !hiddenAsOutdated(property, this.objectValue[key]),
    );
  }

  /** A property's value; the kind of a game option saved before there was one is taken from what is filled in. */
  propertyValue(key: string): unknown {
    if (key === 'kind') return mediaKind(this.schema, this.value) ?? this.objectValue.kind;
    return this.objectValue[key];
  }

  get follows(): boolean {
    return followsSeries(this.schema, this.value);
  }

  /** The address that names the followed series: the overview link, or the card's own link. */
  get followedHref(): string {
    const overview = this.objectValue.overview;
    const href = isRecord(overview) && typeof overview.href === 'string' ? overview.href : '';
    return href || (typeof this.objectValue.href === 'string' ? this.objectValue.href : '');
  }

  get atMinimum(): boolean {
    return this.schema.minItems !== undefined && this.arrayValue.length <= this.schema.minItems;
  }

  get techniqueName(): boolean {
    return this.schema.type === 'string' && isTechniqueName(this.fieldKey, this.parentKey);
  }

  get techniqueOptions(): TechniqueOption[] {
    return techniqueOptions(this.pageValue);
  }

  get categoryList(): boolean {
    return isCategoryList(this.fieldKey, this.parentKey, this.schema);
  }

  get ownErrors(): string[] {
    return this.errors.filter((error) => error.path === this.path).map((error) => error.message.charAt(0).toUpperCase() + error.message.slice(1));
  }

  childPath(key: string): string {
    return this.path ? `${this.path}.${key}` : key;
  }

  /** 1-based, like the paths of `validate`. */
  itemPath(index: number): string {
    return `${this.path}[${index + 1}]`;
  }

  /** A property with its own place in the outline: the page's sections, and groups that hold a list of groups. */
  isPart(property: JsonSchema): boolean {
    return isOwnPart(property, this.path === '' ? 0 : 1);
  }

  isList(schema: JsonSchema): boolean {
    return isObjectList(schema);
  }

  errorCount(path: string): number {
    return errorsWithin(this.errors, path);
  }

  open(path: string): void {
    this.$emit('open', path);
  }

  labelFor = labelFor;
  enumItems = enumItems;
  hasEnumPreviews = hasEnumPreviews;
  mediaUrl = mediaUrl;
  isLinkKey = isLinkKey;

  emit(next: unknown): void {
    this.$emit('input', next);
  }

  pickDate(date: string): void {
    this.dateMenu = false;
    this.emit(date);
  }

  toggleNullable(on: boolean): void {
    this.emit(on && this.nullable ? emptyValue(this.nullable) : null);
  }

  setProperty(key: string, next: unknown): void {
    if (key === 'kind' && typeof next === 'string' && mediaKind(this.schema, this.value) !== null) {
      this.emit(withMediaKind(this.objectValue, next));
      return;
    }
    const update = { ...this.objectValue, [key]: next };
    // A new story or series gets its address from its title, until the address is edited by hand.
    const slug = this.objectValue.slug;
    if (key === 'title' && typeof next === 'string' && this.schema.properties?.slug && (slug === '' || slug === this.autoSlug)) {
      this.autoSlug = slugify(next);
      update.slug = this.autoSlug;
    }
    // Picking a technique used before brings its texts along, unless this week already has its own.
    if (key === 'name' && this.fieldKey === 'technique' && typeof next === 'string') Object.assign(update, this.techniqueTexts(next));
    this.emit(update);
  }

  /** The what, why and steps of a technique used before, for a technique whose texts are still empty. */
  techniqueTexts(name: string): Partial<TechniqueOption> {
    const source = techniqueOptions(this.pageValue).find((option) => techniqueKey(option.name) === techniqueKey(name));
    const value = this.objectValue;
    const steps = Array.isArray(value.detectionSteps) ? value.detectionSteps : [];
    const empty = !value.what && !value.why && steps.every((step) => !step);
    if (!source || !empty) return {};
    return { what: source.what, why: source.why, detectionSteps: [...source.detectionSteps] };
  }

  setItem(index: number, next: unknown): void {
    const items = [...this.arrayValue];
    items[index] = next;
    this.emit(items);
  }

  addItem(): void {
    this.emit([...this.arrayValue, this.schema.items ? emptyValue(this.schema.items) : '']);
  }

  async removeItem(index: number): Promise<void> {
    const item = this.arrayValue[index];
    // An item that was never filled in goes without asking.
    const untouched = this.schema.items && JSON.stringify(item) === JSON.stringify(emptyValue(this.schema.items));
    if (!untouched && !(await askConfirm({ title: `"${itemSummary(item, index)}" verwijderen?`, text: 'Het verdwijnt pas van de site nadat je opslaat.', confirmLabel: 'Verwijderen', danger: true }))) return;
    this.emit(this.arrayValue.filter((_, i) => i !== index));
  }

  moveItem(index: number, step: number): void {
    this.moveItemTo(index, index + step);
  }

  moveItemTo(from: number, to: number): void {
    if (from === to || to < 0 || to >= this.arrayValue.length) return;
    const items = [...this.arrayValue];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    this.emit(items);
  }

  dropClass(index: number): Record<string, boolean> {
    return {
      'is-dragging': this.dragIndex === index,
      'drop-before': this.dropIndex === index && this.dragIndex > index,
      'drop-after': this.dropIndex === index && this.dragIndex >= 0 && this.dragIndex < index,
    };
  }

  onDragStart(index: number, event: DragEvent): void {
    // Only a drag that starts on the handle moves an item.
    if (this.handleDown !== index) return;
    event.stopPropagation();
    this.dragIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  onDragOver(index: number, event: DragEvent): void {
    if (this.dragIndex < 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.dropIndex = index;
  }

  onDrop(index: number, event: DragEvent): void {
    if (this.dragIndex < 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.moveItemTo(this.dragIndex, index);
    this.onDragEnd();
  }

  onDragEnd(): void {
    this.handleDown = -1;
    this.dragIndex = -1;
    this.dropIndex = -1;
  }
}
